// TP header nav — mobile drawer injection + Brands dropdown close guard.
//
// Desktop dropdown behaviour (click toggle, aria-expanded, Esc, outside
// click) is provided by the theme's existing main-nav-click-toggle.js:
// snippets/tp-header-nav.liquid emits the same .navigation-main /
// .has-sub-menu / .navigation-sub-menu structure. One addition lives
// here (wireBrandsClose below): base.css keeps li:focus-within sub-menus
// visible for keyboard nav and a mouse click focuses the trigger link,
// so the Brands panel also needs its focus dropped when it closes.
//
// The Brands + Industries drawer groups live in a <template> rendered by
// tp-header-nav.liquid. This custom element moves them into the drawer's
// ul.navigation-mobile (before the account links, else at the end). The
// theme's drawer expand handler (scripts.js) is delegated on
// document.body, so the injected groups expand with the native drawer UX.
//
// Update-proof: no vendor file is modified; guarded customElements.define
// and idempotent injection keep it safe across theme editor re-renders.

(function () {
  "use strict";

  var INJECTED_ATTR = "data-tp-hn-injected";
  var INJECTED_SELECTOR = "[" + INJECTED_ATTR + "]";

  function removeInjected() {
    document.querySelectorAll(INJECTED_SELECTOR).forEach(function (node) {
      node.remove();
    });
  }

  function inject() {
    var host = document.querySelector("tp-header-nav");
    if (!host) return;

    var template = host.querySelector("template[data-tp-hn-mobile]");
    var drawerList = document.querySelector(".mobile-menu .navigation-mobile");
    if (!template || !drawerList) return;

    // Idempotent: clear any previous injection first.
    removeInjected();

    var frag = template.content.cloneNode(true);
    var items = Array.prototype.slice.call(frag.children);
    if (items.length === 0) return;

    items.forEach(function (li) {
      li.setAttribute(INJECTED_ATTR, "tp-header-nav");
    });

    // Insert before the account group (menu_right) when present, else append.
    var accountLink = drawerList.querySelector('li.navigation-mobile-item a[href*="/account/"]');
    var anchorItem = accountLink ? accountLink.closest("li") : null;

    if (anchorItem && anchorItem.parentElement === drawerList) {
      drawerList.insertBefore(frag, anchorItem);
    } else {
      drawerList.appendChild(frag);
    }
  }

  // ------------------------------------------------------------------------
  // Desktop Brands dropdown: reliable close.
  //
  // base.css keeps `li:focus-within > .navigation-sub-menu` visible for
  // keyboard nav, and a mouse click focuses the trigger link — so
  // main-nav-click-toggle.js removing .is-open alone leaves the panel
  // rendered. tp-header-nav.css hides the panel while the li is not
  // .is-open; the handlers below also drop the focus so the closed
  // state is complete:
  //   - trigger click that closes -> blur the trigger
  //   - click outside the li      -> remove .is-open, aria-expanded=false,
  //                                  blur anything focused inside the li
  //   - Escape                    -> close and return focus to the trigger
  // main-nav-click-toggle.js keeps owning the .is-open toggle for every
  // nav item; these handlers are scoped to the Brands li only (Browse
  // All Products and the main menu are untouched). Idempotent: per-li
  // wiring is guarded by a data attribute, the document listeners bind
  // exactly once, and the li is re-queried at event time so editor
  // re-renders stay covered.
  // ------------------------------------------------------------------------
  var BRANDS_LI_SELECTOR = ".tp-header-nav > li.tp-hn-brands";
  var BRANDS_WIRED_ATTR = "data-tp-hn-brands-wired";
  var brandsDocListenersWired = false;

  function brandsTrigger(li) {
    return li ? li.querySelector(":scope > a") : null;
  }

  function closeBrands(li, returnFocus) {
    if (!li) return;
    li.classList.remove("is-open");
    var link = brandsTrigger(li);
    if (link) link.setAttribute("aria-expanded", "false");
    var active = document.activeElement;
    if (active && li.contains(active) && typeof active.blur === "function") {
      active.blur();
    }
    if (returnFocus && link) link.focus();
  }

  function wireBrandsClose() {
    var li = document.querySelector(BRANDS_LI_SELECTOR);
    var link = brandsTrigger(li);

    if (li && link && li.getAttribute(BRANDS_WIRED_ATTR) !== "1") {
      li.setAttribute(BRANDS_WIRED_ATTR, "1");

      link.addEventListener("click", function (event) {
        // Deferred past the click so main-nav-click-toggle.js's
        // synchronous .is-open toggle lands first regardless of
        // listener registration order; then blur if it closed.
        // event.detail === 0 marks a keyboard (Enter) activation —
        // keep focus on the trigger there; the CSS rule hides the
        // closed panel without the blur.
        var keyboard = event.detail === 0;
        setTimeout(function () {
          if (!li.isConnected || li.classList.contains("is-open")) return;
          link.setAttribute("aria-expanded", "false");
          if (!keyboard && document.activeElement === link) link.blur();
        }, 0);
      });
    }

    if (brandsDocListenersWired) return;
    brandsDocListenersWired = true;

    document.addEventListener("click", function (event) {
      var brandsLi = document.querySelector(BRANDS_LI_SELECTOR);
      if (!brandsLi || !brandsLi.classList.contains("is-open")) return;
      if (brandsLi.contains(event.target)) return;
      closeBrands(brandsLi);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      var brandsLi = document.querySelector(BRANDS_LI_SELECTOR);
      if (!brandsLi || !brandsLi.classList.contains("is-open")) return;
      closeBrands(brandsLi, true);
    });
  }

  class TPHeaderNav extends HTMLElement {
    connectedCallback() {
      inject();
      wireBrandsClose();
    }

    disconnectedCallback() {
      removeInjected();
    }
  }

  if (!customElements.get("tp-header-nav")) {
    customElements.define("tp-header-nav", TPHeaderNav);
  }

  // Theme editor: a header re-render upgrades a fresh <tp-header-nav>
  // (connectedCallback re-injects). The explicit listeners below keep the
  // drawer in sync on shopify:section:load / shopify:section:unload
  // regardless of custom element upgrade order. This event never fires on
  // the storefront.
  document.addEventListener("shopify:section:load", function (event) {
    if (event && event.target && event.target.querySelector && event.target.querySelector("tp-header-nav")) {
      inject();
      wireBrandsClose();
    }
  });

  document.addEventListener("shopify:section:unload", function (event) {
    if (event && event.target && event.target.querySelector && event.target.querySelector("tp-header-nav")) {
      removeInjected();
    }
  });
})();
